// Swedish Massage Frome - Service Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Clock } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const SwedishMassageFrome = () => {
  usePageMeta({
    title: "Swedish Massage Frome | Relaxation Massage Somerset",
    description: "Swedish massage in Frome, Somerset. Classic relaxation massage to release muscle tension, improve circulation, and promote deep rest.",
    canonical: "https://annelielily.com/treatments/swedish-massage"
  });

  const serviceSchema = generateServiceSchema({
    name: "Swedish Massage in Frome",
    description: "Professional Swedish massage therapy in Frome, Somerset. Classic relaxation massage for muscle tension, circulation, and deep relaxation.",
    serviceType: "Massage Therapy",
    url: "https://annelielily.com/treatments/swedish-massage"
  });

  const faqs = [
    {
      question: "What is Swedish massage?",
      answer: "Swedish massage is a classic Western massage technique using long, flowing strokes, kneading, and circular movements. It's designed to relax the whole body, release muscle tension, and improve circulation."
    },
    {
      question: "What should I wear for a massage?",
      answer: "You undress to your comfort level. Most people remove their clothes but keep underwear on. You'll be appropriately draped with towels throughout — only the area being worked on is exposed."
    },
    {
      question: "How long does a session last?",
      answer: "Sessions typically last 60 or 90 minutes. The longer session allows for more thorough work on problem areas or a more leisurely pace throughout."
    },
    {
      question: "Is massage suitable if I have health conditions?",
      answer: "Most conditions are fine, but please let me know about any health issues before your appointment. Some conditions require adaptations, and occasionally massage may not be suitable — I'll advise honestly."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  const benefits = [
    "Releases muscle tension and knots",
    "Improves blood circulation",
    "Reduces stress and anxiety",
    "Promotes deep relaxation",
    "Eases chronic pain",
    "Improves sleep quality"
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Swedish Massage" currentSlug="treatments/swedish-massage" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Massage Therapy in Frome
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Swedish Massage
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Classic massage therapy to release muscle tension, improve circulation, 
              and guide your body into deep relaxation. Sometimes the simplest approach 
              is exactly what's needed.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Clock className="w-4 h-4 text-gold" />
                60-90 min sessions
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Frome, Somerset
              </span>
            </div>
            <Link to="/contact" className="btn-primary">
              Book a Session <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* About */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
            What is Swedish Massage?
          </h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Swedish massage is the foundation of most Western massage therapy. It uses long, 
              flowing strokes, kneading, and circular movements on the superficial layers of 
              muscle — working with the direction of blood flow to the heart.
            </p>
            <p>
              It's different from deep tissue massage (which targets deeper muscle layers and 
              can be more intense) and different from energy work like <Link to="/treatments/reiki-healing" className="text-gold hover:underline">Reiki</Link>. 
              Swedish massage is physical, hands-on therapy designed to relax muscles and 
              improve circulation.
            </p>
            <p>
              If you're carrying physical tension — from stress, desk work, or life in general — 
              massage offers direct relief. Many people find it helps with sleep, reduces anxiety, 
              and leaves them feeling more comfortable in their body.
            </p>
            <p>
              I combine Swedish massage with my understanding of the body from years of 
              teaching <Link to="/yoga-classes" className="text-gold hover:underline">yoga</Link>. This means I work 
              with awareness of how the body holds patterns of tension and how different 
              areas connect.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">
            Benefits of Swedish Massage
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3"
              >
                <Check className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
            What to Expect
          </h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <strong>Before your session:</strong> We'll have a brief chat about any areas of 
              tension, health concerns, and what you're hoping to get from the massage. This 
              helps me tailor the treatment to you.
            </p>
            <p>
              <strong>During the massage:</strong> You'll lie on a comfortable massage table, 
              covered with towels. I work on one area at a time, keeping everything else covered. 
              The room is warm, with soft lighting and gentle music.
            </p>
            <p>
              <strong>Pressure:</strong> I'll check in about pressure — some people prefer 
              lighter work, others want firmer pressure. We find what works for your body.
            </p>
            <p>
              <strong>Afterwards:</strong> You'll have a few minutes to slowly come back. 
              Drink plenty of water for the rest of the day. Most people feel relaxed and 
              perhaps a little floaty — not ideal for rushing back to intense work.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">
            Related Treatments
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Reiki Healing", desc: "Gentle energy work for balance and deep relaxation.", link: "/treatments/reiki-healing" },
              { title: "Chakra Healing", desc: "Working with energy centres to release blocks and restore harmony.", link: "/treatments/chakra-healing" },
              { title: "All Treatments", desc: "View all healing treatments available.", link: "/treatments" },
            ].map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
              >
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                  {service.title}
                </h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Ready to Book?
          </h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Get in touch to book your massage or ask any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book a Session <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
            <a href="tel:07540995021" className="btn-secondary">
              Call 07540 995021
            </a>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SwedishMassageFrome;
